import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import DescriptionMarkdown from "@hl/shared-features/lib/features/markdown/DescriptionMarkdown";
import ListingSource from "@hl/shared-features/lib/features/marketplace/components/ListingSource";
import { useDisabledMarketplaceFF } from "@hl/shared-features/lib/features/marketplace/hooks/useMarketplaces";
import {
  Accordion,
  Box,
  Center,
  createStyles,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import BridgeLinks, {
  BridgeLinkHeader,
  hasBridgeNotice,
} from "~features/MintPage/components/BridgeLinks";
import { TokenTraitContent } from "~features/MintPage/components/DetailSection/TokenTraitContent";
import TokenDetailsCardSection from "~features/token-details/TokenDetailsCardSection";
import TokenDetailsCardSection1155 from "~features/token-details/TokenDetailsCardSection1155";
import TokenDetailsSidebarHistory from "~features/token-details/TokenDetailsSidebarHistory";
import useMarketplaceState from "~features/token-details/hook/useMarketplaceState";
import useMintState from "~hooks/useMintState";

import {
  NftContractStandard,
  SeriesToken,
} from "../../../apollo/graphql.generated";
import { DetailSection } from "../../MintPage/components/DetailSection";
import { DetailSectionColumn } from "../../MintPage/components/DetailSection/DetailSectionColumn";
import { DetailSectionViewFields } from "../../MintPage/components/DetailSection/DetailSectionViewFields";
import TokenDetailsSidebarOffers from "../TokenDetailsSidebarOffers";

import TokenDetailsTitleSection from "./TokenDetailsTitleSection";

const useStyles = createStyles(
  (theme, { hasLoader }: { hasLoader?: boolean }) => ({
    sidebarArea: {
      width: "100%",
      opacity: hasLoader ? 0.5 : 1,
      transition: "opacity 200ms ease-in-out",
      pointerEvents: hasLoader ? "none" : "auto",
      [theme.fn.smallerThan("sm")]: {
        // Footer height is dynamic, so this value should be close to the max height the footer can reach
        paddingBottom: 200,
      },
      [theme.fn.largerThan("sm")]: {
        flex: 5,
        borderLeft: `1px solid ${theme.colors.divider[0]}`,
      },
    },
    sidebarDetails: {
      height: "100%",
      [theme.fn.largerThan("sm")]: {
        padding: "40px 40px 0 40px !important",
      },
    },
    scrollbar: {
      zIndex: 2,
    },
    externalMarketplaceNotice: {
      zIndex: 1,
      position: "sticky",
      bottom: 0,
      background: theme.colors.primaryBackground,
      margin: "0 -40px",
      padding: "20px 40px",
      borderTop: `1px solid ${theme.colors.divider[0]}`,
      marginTop: "auto",
    },
  })
);

const TokenDetailsSidebar = ({
  token,
  tokenLoading,
  modalTokenId,
}: {
  token: SeriesToken;
  tokenLoading?: boolean;
  modalTokenId?: string;
}) => {
  const { collection, isMarketplaceEnabledForCollectionChain, mintVector } =
    useMintState();
  const { classes, theme } = useStyles({ hasLoader: tokenLoading });
  const isMobile = !useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`);
  const { listingSource, setBurnedByAddress, burnedByAddress } =
    useMarketplaceState();
  const isBurned = !!burnedByAddress;
  const disabledMarketplace = useDisabledMarketplaceFF();

  return (
    <Stack
      justify="space-between"
      className={classes.sidebarArea}
      miw={isMobile ? 300 : 380}
    >
      <Stack
        className={classes.sidebarDetails}
        spacing={isMobile ? "lg" : "xl"}
      >
        {!isMobile && <TokenDetailsTitleSection token={token} />}
        {(!isMobile || !!modalTokenId) &&
          isMarketplaceEnabledForCollectionChain && (
            <Center
              mx={-4}
              sx={{
                opacity: isBurned ? 0.5 : 1,
                pointerEvents: isBurned ? "none" : "auto",
              }}
            >
              {collection?.standard === NftContractStandard.ERC1155 ? (
                <TokenDetailsCardSection1155 token={token} />
              ) : (
                <TokenDetailsCardSection token={token} />
              )}
            </Center>
          )}
        <Accordion
          multiple
          defaultValue={["description", "traits", "offers"]}
          styles={{
            label: { fontSize: 16, fontWeight: WEIGHT_BOLD },
            panel: { fontSize: 14 },
            control: { padding: "24px 0" },
            content: { padding: "0 0 25px 0" },
          }}
        >
          <Accordion.Item value="description">
            <Accordion.Control>Description</Accordion.Control>
            <Accordion.Panel>
              <DescriptionMarkdown
                description={token.description || collection?.description || ""}
              />
            </Accordion.Panel>
          </Accordion.Item>
          {(token?.attributes?.length ?? 0) > 0 && (
            <Accordion.Item value="traits">
              <Accordion.Control>Traits</Accordion.Control>
              <Accordion.Panel>
                <Group>
                  {token.attributes?.map((attribute) => (
                    <DetailSectionColumn
                      isV2
                      key={attribute.name}
                      title={
                        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                          {attribute.name}
                        </Text>
                      }
                      content={
                        <TokenTraitContent
                          attributeValue={attribute.value}
                          rarityScore={attribute.rarityScore}
                          showDfsResolutionFailure={
                            collection?.flagVariations.shloms404UI
                          }
                        />
                      }
                    />
                  ))}
                </Group>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {!disabledMarketplace && (
            <Accordion.Item value="offers">
              <Accordion.Control>Offers</Accordion.Control>
              <Accordion.Panel>
                <TokenDetailsSidebarOffers token={token} />
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {isMobile && (
            <Accordion.Item value="history">
              <Accordion.Control>History</Accordion.Control>
              <Accordion.Panel>
                <TokenDetailsSidebarHistory
                  token={token}
                  setBurnedByAddress={setBurnedByAddress}
                />
              </Accordion.Panel>
            </Accordion.Item>
          )}
          <Accordion.Item value="details">
            <Accordion.Control>Details</Accordion.Control>
            <Accordion.Panel>
              <DetailSection
                token={{
                  id: token.tokenId,
                  name: token.name,
                  description: token.description || "",
                  image: token.imageUrl || "",
                  onChainImage: token.animationUrl || "",
                  size: 0,
                  animation_url: token.animationUrl || "",
                  ownerAddress: token.ownerAddress,
                  ownerEns: token.ownerEns,
                }}
              />
              <DetailSectionViewFields
                token={{ id: token.tokenId || undefined }}
                noBottomDivider
                isApollo
              />
            </Accordion.Panel>
          </Accordion.Item>

          {hasBridgeNotice({
            chainId: collection?.chainId ?? 1,
            paymentCurrency: mintVector?.paymentCurrency,
          }) && (
            <Accordion.Item value="new-to-chain">
              <Accordion.Control>
                <BridgeLinkHeader
                  chainId={collection?.chainId ?? 1}
                  paymentCurrency={mintVector?.paymentCurrency}
                  compact
                  size="md"
                />
              </Accordion.Control>
              <Accordion.Panel>
                <BridgeLinks
                  chainId={collection?.chainId ?? 1}
                  paymentCurrency={mintVector?.paymentCurrency}
                  isApollo
                  compact
                />
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>
        {!isMobile && listingSource && listingSource !== "highlight.xyz" && (
          <Box className={classes.externalMarketplaceNotice}>
            <ListingSource size="xs" domain={listingSource} />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default TokenDetailsSidebar;
